import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Button, TextField, Grid } from "@mui/material";

import HomeIcon from "@mui/icons-material/Home";
import makeStyles from "@mui/styles/makeStyles";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { usePlacesWidget } from "react-google-autocomplete";
import { stateFunctions } from "../_helpers/US-States";
import { TimeLoader } from "../_components/loaders/TimeLoader";
import { LifeSafer } from "../_helpers/BrandConstants";
import { getStates } from "../_api/dataListApi";

const useStyles = makeStyles((theme) => ({
  form: {
    width: "100%",
    marginTop: theme.spacing(1),
  },
  submit: { margin: theme.spacing(3, 0, 2) },
  buttonIco: { width: 24, height: 24, marginRight: 10, marginTop: 7 },
  inputIco: { opacity: 0.6, color: "#1c355e", paddingRight: "5px" },
}));

const Address = ({ handleNext, UpdateUserInfo }) => {
  const [showSpinner, setShowSpinner] = useState(false);
  const [address1, setAddress1] = useState("");
  const [city, setCity] = useState("");
  const [county, setCounty] = useState("");
  const [state, setState] = useState("");
  const [stateList, setStateList] = useState(null);
  const [addrLine1Error, setAddrLine1Error] = useState(true);
  const [addrLine1ErrorMessage, setAddrLine1ErrorMessage] =
    useState("Required");
  const [googleValid, setGoogleValid] = useState(false);
  const countryCode = sessionStorage.getItem("LSCountryCode");
  const googleCountryCode = countryCode === "USA" ? "us" : "au";
  const postalCodeLength = countryCode === "USA" ? 5 : 4;
  const { ref: materialRef } = usePlacesWidget({
    apiKey: process.env.REACT_APP_GOOGLE_KEY,
    onPlaceSelected: (place) => updateInputBoxes(place),
    options: {
      types: ["geocode"],
      componentRestrictions: { country: googleCountryCode },
    },
    inputAutocompleteValue: "address1",
  });
  const updateInputBoxes = (data) => {
    let address = data["address_components"].reduce((pre, cur) => {
      if (cur.types.indexOf("postal_code") !== -1) {
        return { ...pre, zip: cur.long_name };
      }
      if (cur.types.indexOf("country") !== -1) {
        return { ...pre, country: cur.long_name };
      }
      if (cur.types.indexOf("administrative_area_level_1") !== -1) {
        return { ...pre, state: cur.long_name };
      }
      if (cur.types.indexOf("administrative_area_level_2") !== -1) {
        return { ...pre, county: cur.long_name };
      }
      if (cur.types.indexOf("locality") !== -1) {
        return { ...pre, city: cur.long_name };
      }
      if (cur.types.indexOf("neighborhood") !== -1) {
        return { ...pre, city: cur.long_name };
      }
      if (cur.types.indexOf("street_number") !== -1) {
        return { ...pre, address: cur.long_name };
      }
      if (cur.types.indexOf("route") !== -1) {
        return { ...pre, street: cur.long_name };
      }
      return pre;
    }, {});
    const streetAddress = data.formatted_address.split(",")[0];
    if (address.county === undefined) address.county = address.city;
    setAddress1(streetAddress);
    setValue("address1", streetAddress);
    setValue("zip", address.zip);
    setValue(
      "cityCountyState",
      `${address.city}, ${address.county}, ${address.state}`
    );
    setCity(address.city);
    setCounty(address.county);
    setState(address.state);
    setAddrLine1ErrorMessage("None");
    setAddrLine1Error(false);
    setGoogleValid(true);
    trigger("cityCountyState");
  };
  const [disableInputs, setDisableInputs] = useState(false);
  const { register, errors, handleSubmit, setValue, trigger } = useForm({
    mode: "onChange",
    reValidateMode: "onChange",
  });
  const { t } = useTranslation();
  const classes = useStyles();

  const submitAddress = (data) => {
    // 1. Do we have an Address?
    if (data.address1.length > 0) {
      // 2. Do we have a City, County, State?
      if (googleValid) {
        setDisableInputs(true);
        setShowSpinner(true);
        let addressInformation = {
          homeAddress1: data.address1,
          homeAddress2: data.address2,
          city: city,
          county: county,
          state: stateFunctions.getStateShortName(state, stateList),
          zip: data.zip,
        };
        UpdateUserInfo(addressInformation);
        handleNext();
      } else {
        setAddrLine1Error(true);
        setAddrLine1ErrorMessage("Directions");
      }
    } else {
      setAddrLine1Error(true);
      setAddrLine1ErrorMessage("Required");
    }
  };

  let countryId = countryCode === "USA" ? 1 : 4;

  useEffect(() => {
    register("address1", { required: true });
    getStates({
      PortalId: 2,
      CountryId: countryId,
      BrandIdentifier: LifeSafer,
      IncludeTest: false,
    })
      .then((stateList) => {
        setStateList(stateList);
      })
      .catch((error) => console.error("error getting state list.", error));
  }, []);

  return (
    <form className={classes.form} onSubmit={handleSubmit(submitAddress)}>
      <TextField
        variant="outlined"
        margin="normal"
        size="small"
        fullWidth
        required
        label={t("registration.homeAddressLine1")}
        autoFocus
        placeholder={t("registration.homeAddressPlaceHolder")}
        name="address1"
        value={address1}
        onChange={(event) => {
          if (event.target.value.length > 0 && googleValid === true) {
            setAddrLine1Error(false);
            setAddrLine1ErrorMessage("None");
          } else {
            setAddrLine1Error(true);
            setAddrLine1ErrorMessage(
              googleValid === true ? "Required" : "Directions"
            );
          }
          const val = event.target.value;
          setValue("address1", val);
          setAddress1(val);
        }}
        disabled={disableInputs}
        error={addrLine1Error}
        helperText={
          addrLine1ErrorMessage === "Required"
            ? t("registration.addressRequired")
            : addrLine1ErrorMessage === "Directions"
            ? t("registration.addressDirections")
            : ""
        }
        inputRef={materialRef}
        InputProps={{
          startAdornment: <HomeIcon className={classes.inputIco} />,
        }}
      />
      <TextField
        variant="outlined"
        margin="normal"
        size="small"
        fullWidth
        disabled={disableInputs}
        label={t("registration.homeAddressLine2")}
        name="address2"
        inputRef={register({})}
        InputProps={{
          startAdornment: <HomeIcon className={classes.inputIco} />,
        }}
      />
      <TextField
        variant="outlined"
        margin="normal"
        size="small"
        fullWidth
        required
        label={t("registration.homeCityCountyState")}
        name="cityCountyState"
        placeholder={t("registration.homeCityCountyStatePlaceHolder")}
        error={!!errors.cityCountyState}
        helperText={
          errors.cityCountyState ? errors.cityCountyState.message : ""
        }
        inputRef={register({
          required: { value: true, message: t("registration.stateRequired") },
        })}
        InputProps={{
          startAdornment: <HomeIcon className={classes.inputIco} />,
          readOnly: true,
          className: "Mui-disabled",
        }}
      />
      <TextField
        variant="outlined"
        margin="normal"
        size="small"
        fullWidth
        required
        label={
          countryCode === "USA"
            ? t("registration.homeZip")
            : t("registration.ausHomeZip")
        }
        name="zip"
        disabled={disableInputs}
        error={!!errors.zip}
        helperText={errors.zip ? errors.zip.message : ""}
        inputRef={register({
          required: {
            value: true,
            message:
              countryCode === "USA"
                ? t("registration.zipRequired")
                : t("registration.ausZipRequired"),
          },
          validate: (value) => {
            if (value.replace(/\D/g, "").length >= postalCodeLength) {
              return true;
            } else {
              return countryCode === "USA"
                ? t("registration.zipValid")
                : t("registration.ausZipValid");
            }
          },
        })}
        InputProps={{
          startAdornment: <HomeIcon className={classes.inputIco} />,
        }}
      />
      <Grid
        container
        direction="column"
        alignContent="center"
        alignItems="center"
      >
        {showSpinner ? (
          <Grid item style={{ padding: 15, margin: 15 }}>
            <TimeLoader />
          </Grid>
        ) : (
          <Grid item style={{ paddingTop: 10, marginTop: 10 }}>
            <Button
              variant="contained"
              color="primary"
              type="submit"
              style={{ width: 240, height: 50 }}
            >
              {t("registration.continueFromAddress")}
            </Button>
          </Grid>
        )}
      </Grid>
    </form>
  );
};

Address.propTypes = {
  userInfo: PropTypes.object,
  UpdateUserInfo: PropTypes.func,
  handleNext: PropTypes.func,
};

export default Address;
